import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    FormControl,
    MenuItem,
    Pagination,
    PaginationItem,
    Select,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

/**
 * Unified pagination controls with three sections:
 * LEFT:   "Showing X-Y of Z {itemLabel}"
 * CENTER: Page navigation
 * RIGHT:  Rows per page selector
 */
export default function PaginationControls({
    page,
    totalPages,
    pageSize,
    totalItems,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = [10, 25, 50],
    itemLabel = 'items',
    loading = false,
    searchTerm = '',
    currentPageCount,
    sx = {},
}) {
    const { t } = useTranslation('shared');
    const isRtl = useTheme().direction === 'rtl';

    if (!totalPages || totalPages <= 1) return null;

    const start = totalItems > 0 ? (page - 1) * pageSize + 1 : 0;
    const end = Math.min(totalItems, page * pageSize);

    const infoText = loading
        ? t('pagination.loading', { page })
        : searchTerm
            ? t('pagination.showingSearch', { count: currentPageCount ?? 0, total: totalItems, searchTerm })
            : t('pagination.showing', { start, end, total: totalItems, itemLabel });

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'center', md: 'center' },
            width: '100%',
            mt: 2,
            mb: 2,
            gap: { xs: 2, md: 1 },
            px: { xs: 1, md: 2 },
            flexShrink: 0,
            ...sx,
        }}>
            {/* Pagination info - LEFT */}
            <Box sx={{
                order: { xs: 3, md: 1 },
                width: { xs: '100%', md: 'auto' },
                textAlign: { xs: 'center', md: 'left' },
                flex: '1 1 auto',
            }}>
                <Typography variant="body2" color="text.secondary">
                    {infoText}
                </Typography>
            </Box>

            {/* Page navigation - CENTER */}
            <Box sx={{
                order: { xs: 1, md: 2 },
                display: 'flex',
                justifyContent: 'center',
                flex: '1 1 auto',
            }}>
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, newPage) => onPageChange(newPage)}
                    color="primary"
                    disabled={loading}
                    size="medium"
                    renderItem={(item) => (
                        <PaginationItem
                            slots={{
                                previous: isRtl ? NavigateNextIcon : NavigateBeforeIcon,
                                next: isRtl ? NavigateBeforeIcon : NavigateNextIcon,
                            }}
                            {...item}
                        />
                    )}
                />
            </Box>

            {/* Row selector - RIGHT */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: { xs: 'center', md: 'flex-end' },
                order: { xs: 2, md: 3 },
                width: { xs: '100%', md: 'auto' },
                flex: '1 1 auto',
            }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1, whiteSpace: 'nowrap' }}>
                    {t('pagination.rowsPerPage')}
                </Typography>
                <FormControl variant="outlined" size="small" sx={{ minWidth: 80 }}>
                    <Select
                        value={pageSize}
                        onChange={(e) => onPageSizeChange(e.target.value)}
                        MenuProps={{ disableScrollLock: true }}
                        displayEmpty
                        disabled={loading}
                        sx={{ height: '32px' }}
                    >
                        {pageSizeOptions.map((opt) => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
        </Box>
    );
}
